import {
  useCompositionsList,
  useCompositionPricing,
  useCurrentComposition
} from "@design-lab/entities/composition";
import { useCombinedProductData } from "@design-lab/entities/product";
import { Loader2, Info } from "lucide-react";
import { TabCard } from "@design-lab/shared/ui/layouts/TabCard";
import { useCallback, useMemo } from "react";
import { useIsSample } from "@design-lab/shared/hooks/useIsSample";
import { useResponsive } from "@design-lab/shared/hooks/useResponsive";
import { useExperiments } from "@design-lab/shared/lib/config";
import { shouldReorderDesignLabTabs } from "@design-lab/shared/lib/experiments";
import { AddToCartToolbar } from "@design-lab/features/add-to-cart";
import { BulkSavings } from "./BulkSavings";
import { SizeSelector } from "./SizeSelector";
import { calculateBasePrice } from "./calculateBasePrice";

export function ManageProductSizing() {
  const isSample = useIsSample();
  const { isDesktop } = useResponsive();
  const { compositions, formCompositions } = useCompositionsList();
  const { getVariantPrice } = useCompositionPricing();
  const { currentComposition, compositionError } = useCurrentComposition();
  const { inventoryData, isLoading, hasError } = useCombinedProductData();
  const experiments = useExperiments();

  const showSubTabs = useMemo(
    () => shouldReorderDesignLabTabs(experiments?.abDesignLabReorderTabs),
    [experiments?.abDesignLabReorderTabs]
  );

  const title = showSubTabs ? "4. Select Size and Quantity" : "3. Select Size and Quantity";

  const variantPrice = useCallback(
    (variantId: string) => currentComposition && getVariantPrice(currentComposition.id, variantId),
    [currentComposition, getVariantPrice]
  );

  const basePrice = currentComposition && calculateBasePrice(currentComposition, variantPrice);

  const loading =
    isLoading ||
    !compositions ||
    compositions.length === 0 ||
    !formCompositions.length ||
    !currentComposition;

  return (
    <TabCard
      title={title}
      showNextButton={!isDesktop}
      footer={isDesktop && showSubTabs && <AddToCartToolbar />}
    >
      {hasError && (
        <p className="text-center text-sm font-semibold text-red-500">Error loading data</p>
      )}
      {loading ? (
        <div>
          <Loader2 size={20} className="mx-auto animate-spin" />
          <p className="text-center text-sm font-semibold">Loading design project</p>
        </div>
      ) : (
        <>
          {compositionError && (
            <div className="order-2 mx-auto flex w-full items-center justify-center gap-2 rounded bg-yellow-50 p-1 text-sm text-yellow-700 lg:order-1">
              <Info />
              <span>{compositionError}</span>
            </div>
          )}

          <div className="order-3 flex flex-col gap-2 lg:order-2">
            {currentComposition && (
              <SizeSelector
                inventoryData={inventoryData}
                isLoading={isLoading}
                compositionId={currentComposition.id}
                basePrice={basePrice}
                getVariantPrice={variantPrice}
              />
            )}
          </div>

          {!isSample && (
            <div className="order-1 lg:order-3" data-testid="bulk-savings-container">
              <BulkSavings />
            </div>
          )}
        </>
      )}
    </TabCard>
  );
}
